import { Visibility } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useField } from 'formik';

import { User, ApprovalMode } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  livePreviewStyle,
  previewContainerStyle,
  previewTextStyle
} from 'styles/pages/ApprovalRuleFormStyle';

import type { Condition } from './ConditionsSection';
import { generateRulePreview } from './ConditionsSection';

export interface LivePreviewSectionProps {
  selectedMode: ApprovalMode;
  selectedApprovers: User[];
  requiredApprovals?: number;
}

export const LivePreviewSection = ({
  selectedMode,
  selectedApprovers,
  requiredApprovals
}: LivePreviewSectionProps) => {
  const { translate } = useTranslations();
  const [{ value: conditions }] = useField('conditions');

  const rulePreview =
    (conditions as Condition[]).length > 0
      ? `${translate('approvals.ifRuleMatches')} ${generateRulePreview(
          conditions as Condition[],
          translate
        )}:`
      : translate('approvals.whenRuleApplies');

  const getApprovalText = () => {
    if (selectedMode === ApprovalMode.AUTO_APPROVE) {
      return translate('approvals.automaticallyApproveTitle');
    }

    if (selectedMode === ApprovalMode.SEQUENTIAL) {
      if (selectedApprovers.length === 0) {
        return `${translate('approvals.requireSequentialApproval')} ${translate('approvals.noApproversAssigned')}`;
      }
      const names = selectedApprovers.map((a: User) => a.name);
      return `${translate('approvals.requireSequentialApproval')} ${names.join(translate('approvals.then'))}`;
    }

    if (selectedMode === ApprovalMode.PARALLEL) {
      if (selectedApprovers.length === 0) {
        return `${translate('approvals.requireParallelApproval')} ${translate('approvals.noApproversAssigned')}`;
      }
      const count = requiredApprovals ?? 1;
      const total = selectedApprovers.length;
      const names = selectedApprovers.map((a: User) => a.name);
      return `${translate('approvals.requireParallelApproval')} ${count} ${translate('labels.of')} ${total} ${translate('approvals.approvalsFrom')}: ${names.join(', ')}`;
    }

    return '';
  };

  const [{ value: exclusive }] = useField('exclusive');

  const rulePreviewText = `${rulePreview} ${getApprovalText()}`;

  const exclusiveWarning = exclusive
    ? `. ${translate('approvals.livePreviewExclusiveWarning')}`
    : '';

  return (
    <Box sx={livePreviewStyle}>
      <Box sx={previewContainerStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Visibility sx={{ color: '#9ca3af', fontSize: '0.875rem' }} />
          <Typography
            variant="body2"
            sx={{
              color: '#9ca3af',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}
          >
            {translate('approvals.livePreview')}
          </Typography>
        </Box>
        <Typography
          sx={previewTextStyle}
        >{`${rulePreviewText}${exclusiveWarning}`}</Typography>
      </Box>
    </Box>
  );
};
